/* ============================================================
   MA Subscription Plans
   - Renders plan selection cards for Recharge / Stay.ai
   - Both apps use Shopify's native selling_plan_groups API
   - Detects variant changes via document change events + rAF
   - Updates the product form's selling_plan input on selection
   ============================================================ */

(function () {
  'use strict';

  class SubscriptionPlans extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this._selectedGiftVariantId = null;
      this._currentPlanId = null;

      // Parse all variant+plan allocation data embedded as JSON
      // Block context uses section.id + block.id; section context uses section.id only
      const dataEl =
        this.querySelector('[id^="SubPlansData-"]') ||
        document.getElementById(`SubPlansData-${this.sectionId}`);
      this.allocations = dataEl ? JSON.parse(dataEl.textContent) : [];

      const cartGiftsEl = this.querySelector('[id^="SubPlansCartGifts-"]');
      this._cartGifts = cartGiftsEl ? JSON.parse(cartGiftsEl.textContent) : [];

      this._bindCards();
      this._initVariantListener();
      this._initGiftInterception();
      this._initGiftDetails();
      this._initDefaultSelection();
    }

    // ── Card selection ──────────────────────────────────────────

    _bindCards() {
      this.querySelectorAll('.ma-sub-plans__card').forEach((card) => {
        card.addEventListener('click', () => this._selectCard(card));
      });
    }

    _selectCard(card) {
      this.querySelectorAll('.ma-sub-plans__card').forEach((c) =>
        c.classList.remove('is-selected')
      );
      card.classList.add('is-selected');

      const radio = card.querySelector('input[type="radio"]');
      if (radio) radio.checked = true;

      const planId = card.dataset.planId || '';
      this._currentPlanId = planId;
      this._updateSellingPlanInput(planId);

      // Only the plan-specific gift is added programmatically.
      // Cart threshold gifts are handled automatically by Rebuy/cart automations.
      this._selectedGiftVariantId = card.dataset.giftVariantId || null;
      this._selectedGiftItems = [];
      if (this._selectedGiftVariantId) {
        this._selectedGiftItems.push({
          id: parseInt(this._selectedGiftVariantId, 10),
          quantity: 1,
          selling_plan: card.dataset.giftSellingPlanId ? parseInt(card.dataset.giftSellingPlanId, 10) : null,
        });
      }
      const gift2VariantId = card.dataset.gift2VariantId || null;
      if (gift2VariantId) {
        this._selectedGiftItems.push({
          id: parseInt(gift2VariantId, 10),
          quantity: 1,
          selling_plan: card.dataset.gift2SellingPlanId ? parseInt(card.dataset.gift2SellingPlanId, 10) : null,
        });
      }
    }

    _initDefaultSelection() {
      // Use the card already marked selected by Liquid (visible only),
      // otherwise pick the first visible card
      const selected =
        this.querySelector('.ma-sub-plans__card.is-selected:not([hidden])') ||
        this.querySelector('.ma-sub-plans__card:not([hidden])');

      if (selected) this._selectCard(selected);
    }

    // ── Selling plan input in the product form ──────────────────

    _updateSellingPlanInput(planId) {
      const form = this._getProductForm();
      if (!form) return;

      let input = form.querySelector('input[name="selling_plan"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        form.appendChild(input);
      }

      if (planId) {
        input.value = planId;
        input.disabled = false;
      } else {
        // One-time purchase — remove selling_plan from form submission
        input.value = '';
        input.disabled = true;
      }
    }

    _getProductForm() {
      // Prefer the explicit form ID set by Liquid (product-form-{section.id})
      const formId = this.dataset.formId;
      if (formId) {
        const byId = document.getElementById(formId);
        if (byId) return byId;
      }
      // Fallback: nearest product form in the DOM
      return (
        this.closest('.product__info-container')?.querySelector('form[action="/cart/add"]') ||
        document.querySelector('form[action="/cart/add"]')
      );
    }

    // ── Variant change — live price updates ────────────────────

    _initVariantListener() {
      // Dawn's subscribe/publish are ES module-scoped, not globals — can't use them here.
      // Instead: listen for any change event on the document, then check if the product
      // form's hidden variant ID input actually changed value.
      const form = this._getProductForm();
      if (!form) return;

      this._lastVariantId = form.querySelector('input[name="id"]')?.value;

      this._variantChangeHandler = () => {
        // rAF lets Dawn's own event handlers run first and update input[name="id"]
        requestAnimationFrame(() => {
          const currentId = form.querySelector('input[name="id"]')?.value;
          if (currentId && currentId !== this._lastVariantId) {
            this._lastVariantId = currentId;
            this._updatePricesForVariant(currentId);
          }
        });
      };

      document.addEventListener('change', this._variantChangeHandler);
    }

    // ── Gift auto-add on purchase ────────────────────────────────
    // Intercepts the product form submit and issues a single /cart/add.js
    // request containing both the subscription and gift items so they land
    // in the cart as linked lines (parent_id establishes the relationship),
    // matching how the quiz wizard adds gifts.

    _initGiftInterception() {
      const form = this._getProductForm();
      if (!form) return;

      this._giftSubmitHandler = async (e) => {
        // Always re-assert the correct selling plan right before submission.
        if (this._currentPlanId !== null) {
          this._updateSellingPlanInput(this._currentPlanId);
        }

        const giftItems = this._selectedGiftItems || [];

        // No gifts on this plan — let Dawn's ProductForm handle it normally.
        if (!giftItems.length) return;

        // Take over the full cart add so subscription + gifts go in one request.
        e.preventDefault();
        e.stopImmediatePropagation();

        const formData = new FormData(form);
        const variantId = parseInt(formData.get('id'), 10);
        const sellingPlan = formData.get('selling_plan');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;

        const btn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (btn) btn.disabled = true;

        const subscriptionItem = {
          id: variantId,
          quantity,
          properties: { '_includes_gift': giftItems.map((g) => g.id).join(',') },
        };
        if (sellingPlan) subscriptionItem.selling_plan = parseInt(sellingPlan, 10);

        const giftLineItems = giftItems.map((g) => {
          const item = {
            id: g.id,
            quantity: 1,
            parent_id: variantId,
            properties: { '_is_free_gift': 'true', '_for_variant': variantId.toString() },
          };
          if (g.selling_plan) item.selling_plan = g.selling_plan;
          return item;
        });

        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [subscriptionItem, ...giftLineItems] }),
          });
          // Reuse the same event the quiz wizard fires — cart drawer handles refresh + open.
          document.dispatchEvent(new CustomEvent('ma:quiz-cart-drawer-open'));
        } catch (err) {
          console.warn('[MA] Failed to add to cart:', err);
        } finally {
          if (btn) btn.disabled = false;
        }
      };

      // Capture phase so we fire before Dawn's ProductForm bubble-phase handler.
      form.addEventListener('submit', this._giftSubmitHandler, true);
    }

    // ── Gift details overlay (gift-block variant) ───────────────

    _initGiftDetails() {
      this.querySelectorAll('.ma-sub-plans__gift-details-btn').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const card = btn.closest('.ma-sub-plans__card');
          if (!card) return;
          const gifts = this._getCardGifts(card);
          const planName = card.querySelector('.ma-sub-plans__plan-name')?.textContent?.trim() || 'this plan';
          this._openGiftOverlay(planName, gifts);
        });
      });
      this._updateGiftCounts();
    }

    _updateGiftCounts() {
      this.querySelectorAll('.ma-sub-plans__gift-trigger').forEach((trigger) => {
        const card = trigger.closest('.ma-sub-plans__card');
        if (!card) return;
        const count = this._getCardGifts(card).length;
        const el = trigger.querySelector('[data-gift-count]');
        if (el) el.textContent = `+ ${count} Free Gift${count !== 1 ? 's' : ''}`;
      });
    }

    _getCardGifts(card) {
      const gifts = [];
      const label = card.dataset.giftLabel;
      if (label) {
        gifts.push({
          label,
          detail: card.dataset.giftDetail || '',
          image_url: card.dataset.giftImageUrl || '',
        });
      }
      const label2 = card.dataset.gift2Label;
      if (label2) {
        gifts.push({
          label: label2,
          detail: card.dataset.gift2Detail || '',
          image_url: card.dataset.gift2ImageUrl || '',
        });
      }
      const billedCents = parseInt(card.dataset.price, 10) || 0;
      for (const cg of (this._cartGifts || [])) {
        if (cg.enabled && cg.variant_id && billedCents >= cg.threshold) {
          gifts.push({
            label: cg.label || 'Bonus Gift',
            detail: cg.detail || '',
            image_url: cg.image_url || '',
          });
        }
      }
      return gifts;
    }

    _openGiftOverlay(planName, gifts) {
      document.querySelector('.ma-sub-plans__gift-overlay')?.remove();

      const isDesktop = window.matchMedia('(min-width: 750px)').matches;

      const overlay = document.createElement('div');
      overlay.className = 'ma-sub-plans__gift-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', `Free gifts included with ${planName}`);

      const sheet = document.createElement('div');
      sheet.className = 'ma-sub-plans__gift-sheet';
      sheet.addEventListener('click', (e) => e.stopPropagation());

      const close = () => overlay.remove();
      overlay.addEventListener('click', close);

      const onKeyDown = (e) => {
        if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKeyDown); }
      };
      document.addEventListener('keydown', onKeyDown);
      overlay.addEventListener('click', () => document.removeEventListener('keydown', onKeyDown));

      if (!isDesktop) {
        const handle = document.createElement('div');
        handle.className = 'ma-sub-plans__gift-sheet-handle';
        sheet.appendChild(handle);
      }

      const header = document.createElement('div');
      header.className = 'ma-sub-plans__gift-sheet-header';

      const headerText = document.createElement('div');
      const overline = document.createElement('div');
      overline.className = 'ma-sub-plans__gift-sheet-overline';
      overline.textContent = `🎁 Free with ${planName}`;
      const title = document.createElement('div');
      title.className = 'ma-sub-plans__gift-sheet-title';
      title.textContent = `${gifts.length} Free Gift${gifts.length > 1 ? 's' : ''} Included`;
      headerText.appendChild(overline);
      headerText.appendChild(title);

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'ma-sub-plans__gift-sheet-close';
      closeBtn.setAttribute('aria-label', 'Close');
      closeBtn.textContent = '✕';
      closeBtn.addEventListener('click', close);

      header.appendChild(headerText);
      header.appendChild(closeBtn);
      sheet.appendChild(header);

      const list = document.createElement('div');
      list.className = 'ma-sub-plans__gift-list';
      gifts.forEach((g) => {
        const item = document.createElement('div');
        item.className = 'ma-sub-plans__gift-item';

        const thumb = document.createElement('div');
        thumb.className = 'ma-sub-plans__gift-item-thumb';
        if (g.image_url) {
          const img = document.createElement('img');
          img.src = g.image_url;
          img.alt = '';
          thumb.appendChild(img);
        } else {
          thumb.textContent = '🎁';
        }

        const info = document.createElement('div');
        const nameEl = document.createElement('div');
        nameEl.className = 'ma-sub-plans__gift-item-name';
        nameEl.textContent = g.label;
        info.appendChild(nameEl);
        if (g.detail) {
          const detailEl = document.createElement('div');
          detailEl.className = 'ma-sub-plans__gift-item-detail';
          detailEl.textContent = g.detail;
          info.appendChild(detailEl);
        }

        item.appendChild(thumb);
        item.appendChild(info);
        list.appendChild(item);
      });
      sheet.appendChild(list);

      const gotIt = document.createElement('button');
      gotIt.type = 'button';
      gotIt.className = 'ma-sub-plans__gift-got-it';
      gotIt.textContent = 'Got it';
      gotIt.addEventListener('click', close);
      sheet.appendChild(gotIt);

      overlay.appendChild(sheet);
      document.body.appendChild(overlay);
      closeBtn.focus();
    }

    disconnectedCallback() {
      if (this._variantChangeHandler) {
        document.removeEventListener('change', this._variantChangeHandler);
      }
      if (this._giftSubmitHandler) {
        const form = this._getProductForm();
        if (form) form.removeEventListener('submit', this._giftSubmitHandler, true);
      }
    }

    _updatePricesForVariant(variantId) {
      let selectedCardHidden = false;

      // Find the monthly (1-month) plan price for this variant — used as savings baseline
      const monthlyEntry = this.allocations.find(
        (a) => String(a.variant_id) === String(variantId) && a.months === 1 && a.plan_id !== null
      );
      const monthlyBaseline = monthlyEntry ? monthlyEntry.price : null;

      this.querySelectorAll('.ma-sub-plans__card').forEach((card) => {
        const planIdRaw = card.dataset.planId;
        const planId = planIdRaw ? parseInt(planIdRaw, 10) : null;

        const entry = this.allocations.find(
          (a) => String(a.variant_id) === String(variantId) && a.plan_id === planId
        );

        // Hide cards whose plan doesn't apply to this variant
        if (!entry) {
          card.hidden = true;
          if (card.classList.contains('is-selected')) selectedCardHidden = true;
          return;
        }

        card.hidden = false;

        // months_divisor is set by Liquid on data-months — constant per plan
        const months = parseInt(card.dataset.months, 10) || 1;
        const monthlyPrice   = Math.round(entry.price / months);
        const compareMonthly = Math.round(entry.compare_at_price / months);

        // Update data attributes
        card.dataset.price = entry.price;
        card.dataset.compareAtPrice = entry.compare_at_price;

        // Update per-month price display
        const priceEl = card.querySelector('.ma-sub-plans__per-delivery-price');
        if (priceEl) {
          priceEl.dataset.money = monthlyPrice;
          priceEl.textContent = this._formatMoney(monthlyPrice);
        }

        // Update / show / hide prepaid total
        const billedEl = card.querySelector('.ma-sub-plans__billed-total');
        if (billedEl) {
          if (months > 1) {
            billedEl.dataset.money = entry.price;
            billedEl.textContent = `${this._formatMoney(entry.price)} billed total`;
            billedEl.hidden = false;
          } else {
            billedEl.hidden = true;
          }
        }

        // Update compare-at price
        const compareEl = card.querySelector('.ma-sub-plans__compare-price');
        if (compareEl) {
          if (compareMonthly > monthlyPrice) {
            compareEl.dataset.money = compareMonthly;
            compareEl.textContent = this._formatMoney(compareMonthly);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }

        // Update savings vs monthly baseline
        const savingsEl = card.querySelector('.ma-sub-plans__savings');
        if (savingsEl) {
          let savingsPct = 0;
          if (monthlyBaseline && months > 1 && monthlyBaseline > monthlyPrice) {
            savingsPct = Math.round(((monthlyBaseline - monthlyPrice) * 100) / monthlyBaseline);
          }
          if (savingsPct > 0) {
            savingsEl.textContent = `Save ${savingsPct}%`;
            savingsEl.hidden = false;
          } else {
            savingsEl.hidden = true;
          }
        }
      });

      // If the selected plan is no longer valid for the new variant, pick the first visible one
      if (selectedCardHidden) {
        const firstVisible = this.querySelector('.ma-sub-plans__card:not([hidden])');
        if (firstVisible) this._selectCard(firstVisible);
      }

      this._updateGiftCounts();
    }

    // ── Money formatting ────────────────────────────────────────
    // Uses Shopify's theme money format if available, otherwise cents → dollars

    _formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
      }
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(cents / 100);
    }
  }

  customElements.define('subscription-plans', SubscriptionPlans);
})();
